import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Typography,
    Box,
    Fab,
    Tooltip
} from '@mui/material';
import { Close, AddPhotoAlternate, CloudUpload } from '@mui/icons-material';
import ImageUploadManager from "./ImageUploadManager";
import { ArtworkImageStub } from "../../models/Artwork";
import { ValidationResult } from "./validateImageFiles";
interface ImageUploadWithDialogProps {
    // Trigger button props
    triggerButton?: React.ReactNode;
    showTriggerButton?: boolean;
    triggerButtonPosition?: 'fixed' | 'absolute' | 'static';

    // Dialog props
    dialogTitle?: string;
    dialogMaxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    showDialog: boolean;
    onDialogClose: () => void;

    onImagesUpdate: (changes: {
        exisiting?: ArtworkImageStub[],
        added?: File[];
        removed?: number[];
    }) => void;
    pendingChanges?: {
        exisiting: ArtworkImageStub[];
        added: File[];
        removed: number[];
    };

    // All original ImageUpload props
    existingImages?: ArtworkImageStub[];
    onImagesChange?: (files: File[], validationResult: ValidationResult | null) => void;
    onExistingImagesChange?: (images: ArtworkImageStub[]) => void;
    removedImages?: (id: number[]) => void;
    multiple?: boolean;
    maxFiles?: number;
}

const ImageUploadWithDialog: React.FC<ImageUploadWithDialogProps> = ({
    // Dialog trigger props
    triggerButton,
    showTriggerButton = false,
    triggerButtonPosition = 'static',

    // Dialog props
    dialogTitle = "Manage Images",
    dialogMaxWidth = 'md',
    showDialog = false,
    onDialogClose,

    onImagesUpdate,
    pendingChanges = { existing: [], added: [], removed: [] },

    // ImageUpload props
    existingImages = [],
    onImagesChange,
    onExistingImagesChange,
    removedImages,
    multiple = true,
    maxFiles,
    ...imageUploadProps
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [localUploadedFiles, setLocalUploadedFiles] = useState<File[]>([]);
    const [localExistingImages, setLocalExistingImages] = useState(existingImages);
    const [removedImageIds, setRemovedImageIds] = useState<number[]>([]);

    const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

    const handleOpen = () => setDialogOpen(true);

    const handleClose = () => {
        setRemovedImageIds([]);
        setLocalUploadedFiles([]);
        setLocalExistingImages(existingImages);
        setDialogOpen(false);
        onDialogClose();
    };

    const handleSave = () => {
        onImagesChange?.(localUploadedFiles, validationResult);
        onExistingImagesChange?.(localExistingImages);
        removedImages?.(removedImageIds);

        //Combine Updates
        onImagesUpdate({
            exisiting: localExistingImages,
            added: localUploadedFiles,
            removed: removedImageIds
        })

        handleClose();
    };

    const handleImagesChange = (files: File[]) => {
        setLocalUploadedFiles(files);
    };

    const handleExistingImagesChange = (images: ArtworkImageStub[]) => {
        setLocalExistingImages(images);
    };

    const handleRemoveExistingImage = (id: number) => {
        setRemovedImageIds((prev) => [...prev, id]);
    };

    const DefaultTriggerButton = (
        <Fab
            variant="extended"
            color="primary"
            onClick={handleOpen}
            sx={{
                position: triggerButtonPosition === 'fixed' ? 'fixed' :
                    triggerButtonPosition === 'absolute' ? 'absolute' : 'static',
                bottom: triggerButtonPosition === 'fixed' ? 24 : 'auto',
                right: triggerButtonPosition === 'fixed' ? 24 : 'auto',
                zIndex: triggerButtonPosition === 'fixed' ? 1000 : 'auto'
            }}
        >
            <CloudUpload sx={{ mr: 1 }} />
            Manage Images
        </Fab>
    );

    const DefaultButton = (

        <button className="responsive-button" onClick={handleOpen}>
            <CloudUpload sx={{ mr: 1 }} />
            Manage Images
        </button>
    )

    return (
        <>
            {/* Trigger Button */}
            {showTriggerButton && (
                triggerButton || DefaultButton
            )}

            {/* Dialog */}
            <Dialog
                open={dialogOpen || showDialog}
                onClose={handleClose}
                maxWidth={dialogMaxWidth}
                fullWidth
                PaperProps={{
                    sx: { minHeight: '70vh', maxHeight: '90vh' }
                }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6" component="div">
                            {dialogTitle}
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 0, overflow: 'hidden' }}>
                    <ImageUploadManager
                        existingImages={localExistingImages}
                        onImagesChange={handleImagesChange}
                        onExistingImagesChange={handleExistingImagesChange}
                        onRemoveImage={handleRemoveExistingImage}
                        multiple={multiple}
                        maxFiles={maxFiles}
                        showFileList={true}
                        showThumbnails={true}
                        thumbnailSize={80}
                        elevation={0}
                        {...imageUploadProps}
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleClose} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={localUploadedFiles.length === 0 && removedImageIds.length === 0}
                        startIcon={<AddPhotoAlternate />}
                    >
                        Upload {localUploadedFiles.length > 0 && `(${localUploadedFiles.length})`}
                        {removedImageIds.length > 0 && ` Remove (${removedImageIds.length})`}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ImageUploadWithDialog;